import { useRef } from 'react';

/** The per-item fields the version below tracks. See {@link useItemsVersion}. */
interface VersionedItem {
  value: string;
  disabled?: boolean;
}

/**
 * A version number for a list of DS items that changes ONLY when the items'
 * `value`/`disabled` tuples change — not when the caller hands over a fresh
 * array with the same contents.
 *
 * Call sites write `items` inline (`items={TABS.map(…)}`), which is idiomatic and
 * which no component should have to forbid. Listed raw as a dependency, that
 * array turns every memo and every measure effect keyed on it into pure
 * overhead: the work re-runs on every render of the surrounding screen.
 *
 * Comparing element-wise against a stored projection costs no allocation in the
 * steady state (the lists here are a handful of items), which a `join()`-based
 * key cannot claim — it allocates an array and a string on every render to
 * discover that nothing changed.
 *
 * SCOPE, and it matters for correctness: the version covers `value` and
 * `disabled` ONLY. Anything keyed on it must not read a per-item `label`, `icon`
 * or `accessibilityLabel`, because those can change while the version does not.
 * Render such fields straight from `items` instead.
 */
export function useItemsVersion(items: readonly VersionedItem[]): number {
  const previous = useRef<{ value: string; disabled: boolean }[]>([]);
  const version = useRef(0);

  let same = previous.current.length === items.length;
  if (same) {
    for (let i = 0; i < items.length; i += 1) {
      const before = previous.current[i];
      if (before.value !== items[i].value || before.disabled !== !!items[i].disabled) {
        same = false;
        break;
      }
    }
  }
  if (!same) {
    previous.current = items.map((item) => ({
      value: item.value,
      disabled: !!item.disabled,
    }));
    version.current += 1;
  }
  return version.current;
}
